import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';

import MsvCardGrid from 'components/upb_mfr_viewer/MsvCardGrid';
import useMfrManufacturers from 'components/upb_mfr_viewer/hooks/useMfrManufacturers';
import { constructUrlPathSegment } from 'components/upb_viewer/UPBViewer';
import BetaPagination from 'components/upb_viewer_beta/BetaPagination';
import {
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_MEDIUM,
} from 'components/upb_viewer_beta/css/betaTokens';
import { DEFAULT_UPB_PAGE_SIZE } from 'lib/constants';

/**
 * MfrManufacturersPage — MSV home grid: manufacturers ordered by GMV, scoped by
 * the sidebar category filter, the KPI-strip distributor filter, and the top-bar
 * search. Clicking a card drills into that manufacturer's brands.
 */
function MfrManufacturersPage({
  searchQuery,
  l1CategoryId,
  l2CategoryId,
  distributorId,
  thirdPartyTag,
  onNavigate,
  baseURL,
}) {
  const [currentPage, setCurrentPage] = useState(1);
  const page = useMemo(() => Math.max(currentPage - 1, 0), [currentPage]);
  const offset = useMemo(() => page * DEFAULT_UPB_PAGE_SIZE, [page]);

  // Reset paging whenever the active filter set changes.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, l1CategoryId, l2CategoryId, distributorId, thirdPartyTag]);

  const { data: { manufacturers = [], count = 0 } = {}, loading } = useMfrManufacturers({
    offset,
    limit: DEFAULT_UPB_PAGE_SIZE,
    searchQuery,
    l1CategoryId,
    l2CategoryId,
    distributorId,
    thirdPartyTag,
  });

  const totalPages = Math.max(Math.ceil(count / DEFAULT_UPB_PAGE_SIZE), 1);
  const startIdx = count === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + DEFAULT_UPB_PAGE_SIZE, count);

  const handleClick = mfr => {
    const seg = constructUrlPathSegment({ value: mfr.id, label: mfr.name });
    onNavigate(`/${baseURL}/${seg}`);
  };

  return (
    <div className={css(styles.page)}>
      {/* Title + distributor filter live in the container's header card (mock);
          this row only carries the paging count. */}
      <div className={css(styles.head)}>
        <span className={css(styles.count)}>
          {count === 0
            ? 'No manufacturers'
            : `${startIdx}–${endIdx} of ${count.toLocaleString('en-US')}`}
        </span>
      </div>

      <MsvCardGrid
        items={manufacturers}
        loading={loading}
        onItemClick={handleClick}
        emptyLabel="No manufacturers found."
      />

      {totalPages > 1 && (
        <BetaPagination
          totalPages={totalPages}
          currentPageNumber={page}
          onPageChange={newPage => setCurrentPage(newPage + 1)}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    minWidth: 0,
  },

  head: {
    display: 'flex',
    justifyContent: 'flex-end',
  },

  count: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    color: FG_3,
  },
});

MfrManufacturersPage.propTypes = {
  searchQuery: PropTypes.string,
  l1CategoryId: PropTypes.number,
  l2CategoryId: PropTypes.number,
  distributorId: PropTypes.number,
  thirdPartyTag: PropTypes.string,
  onNavigate: PropTypes.func.isRequired,
  baseURL: PropTypes.string.isRequired,
};

MfrManufacturersPage.defaultProps = {
  searchQuery: '',
  l1CategoryId: null,
  l2CategoryId: null,
  distributorId: null,
  thirdPartyTag: null,
};

export default MfrManufacturersPage;
